import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import {
  SITE_TYPES,
  LANGUAGES,
  PRICES,
  Filters,
  SortingMethod,
} from '@common/types';
import { isValidSortingMethod } from '@utils/typeUtils';

export interface TemplatesContextProviderProps {
  children?: ReactNode;
  numberOfResults: number;
  numberOfPages: number;
  isMobileUserAgent?: boolean;
  entry?: string;
  origin?: string;
  host?: string;
  filters: Filters;
  currentPage: number;
  sort: SortingMethod;
  error?: any;
}

type State = {
  isMobileUserAgent?: boolean;
  numberOfResults: number;
  numberOfPages: number;
  currentPage: number;
  entry?: string;
  origin?: string;
  host?: string;
  filterOpen: boolean;
  filters: Filters;
  sort: SortingMethod;
  SSRFlow: boolean;
  appStatus: string;
  error?: any;
};

interface TemplatesContextType {
  state: State;
  dispatch: (arg: any) => void;
}

const TemplatesContext = createContext<TemplatesContextType | null>(null);

export const APP_STATUS = {
  LOADING: 'loading',
  INITIALIZED: 'initialized',
  ERROR: 'error',
};

function mainReducer(state: State, action: any) {
  switch (action.type) {
    case ACTIONS.SET_TEMPLATES: {
      return {
        ...state,
        templates: action.templates,
      };
    }
    case ACTIONS.SET_NUMBER_OF_PAGES: {
      return {
        ...state,
        numberOfPages: action.numberOfPages,
      };
    }
    case ACTIONS.TOGGLE_FILTER: {
      return {
        ...state,
        filterOpen: !state.filterOpen,
      };
    }
    case ACTIONS.SET_FILTERS: {
      const filters = action.filters;
      return {
        ...state,
        filters,
      };
    }
    case ACTIONS.SET_PAGE: {
      const { currentPage } = action;
      return {
        ...state,
        currentPage,
      };
    }
    case ACTIONS.SET_SORT: {
      const { sort } = action;
      return {
        ...state,
        sort,
      };
    }
    case ACTIONS.LOADING: {
      return {
        ...state,
        SSRFlow: false,
        appStatus: APP_STATUS.LOADING,
      };
    }
    case ACTIONS.INITIALIZED: {
      return {
        ...state,
        appStatus: APP_STATUS.INITIALIZED,
      };
    }
    case ACTIONS.ERROR: {
      return {
        ...state,
        appStatus: APP_STATUS.ERROR,
      };
    }
    default:
      throw new Error();
  }
}

export const ACTIONS = {
  SET_NUMBER_OF_PAGES: 'SET_NUMBER_OF_PAGES',
  SET_TEMPLATES: 'SET_TEMPLATES',
  TOGGLE_FILTER: 'TOGGLE_FILTER',
  FETCH_MORE_TEMPLATES: 'FETCH_MORE_TEMPLATES',
  SET_MORE_TEMPLATES: 'SET_MORE_TEMPLATES',
  SET_FILTERS: 'SET_FILTERS',
  SET_PAGE: 'SET_PAGE',
  SET_SORT: 'SET_SORT',
  LOADING: 'LOADING',
  INITIALIZED: 'INITIALIZED',
  ERROR: 'ERROR',
};

export function TemplatesContextProvider(props: TemplatesContextProviderProps) {
  const [state, dispatch] = useReducer(mainReducer, {
    appStatus: 'initialized',
    SSRFlow: true,
    numberOfResults: props.numberOfResults,
    numberOfPages: props.numberOfPages,
    currentPage: _.toNumber(props.currentPage),
    entry: props.entry,
    origin: props.origin,
    host: props.host,
    filters: _.defaults(props.filters, {
      price: PRICES.ALL,
      language: LANGUAGES.ALL,
      siteType: SITE_TYPES.ALL,
    }),
    filterOpen: true,
    sort: props.sort || 'relevance',
    isMobileUserAgent: props.isMobileUserAgent,
    error: props.error,
  });

  const router = useRouter();

  function updateFiltersFromUrl(searchParams: URLSearchParams): any {
    const price = searchParams.get('price') || PRICES.ALL;
    const stylesFromUrl = searchParams.get('styles') || null;
    const language = searchParams.get('language') || LANGUAGES.ALL;
    const languagesFromUrl = searchParams.get('languages') || null;
    const featuresFromUrl = searchParams.get('features') || null;
    const industriesFromUrl = searchParams.get('industries') || null;
    const siteType = searchParams.get('siteType') || SITE_TYPES.ALL;

    dispatch({
      type: ACTIONS.SET_FILTERS,
      filters: {
        styles: _.isNull(stylesFromUrl) ? [] : stylesFromUrl.split(','),
        features: _.isNull(featuresFromUrl) ? [] : featuresFromUrl.split(','),
        industries: _.isNull(industriesFromUrl)
          ? []
          : industriesFromUrl.split(','),
        languages: _.isNull(languagesFromUrl)
          ? []
          : languagesFromUrl.split(','),
        price,
        siteType,
        language,
      },
    });
    return price;
  }

  function updateCurrentPageFromUrl(searchParams: URLSearchParams): any {
    const currentPage = _.toNumber(searchParams.get('page')) || 1;
    dispatch({ type: ACTIONS.SET_PAGE, currentPage });

    return currentPage;
  }

  function updateSortFromUrl(searchParams: URLSearchParams): any {
    const sortFromUrl = searchParams.get('sort');
    const sort = isValidSortingMethod(sortFromUrl) ? sortFromUrl : 'relevance';
    dispatch({ type: ACTIONS.SET_SORT, sort });

    return sort;
  }

  useEffect(() => {
    const handleRouterChange = async (queryParams: string) => {
      const url = new URL(`${state.host}${queryParams}`);
      updateFiltersFromUrl(url.searchParams);
      updateCurrentPageFromUrl(url.searchParams);
      updateSortFromUrl(url.searchParams);
    };
    router.events.on('routeChangeComplete', handleRouterChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouterChange);
    };
  }, [router]);

  return (
    <TemplatesContext.Provider value={{ state, dispatch }}>
      {props.children}
    </TemplatesContext.Provider>
  );
}

export function useTemplatesContext() {
  const appContext = useContext(TemplatesContext);
  if (!appContext) {
    throw new Error(
      'useTemplatesContext must be used within the TemplatesContext.Provider',
    );
  }
  return appContext;
}
